import React, { useMemo, useState, Component } from 'react';
import { Brain, Star, Briefcase, Heart, Package, Users, TrendingUp, Download } from 'lucide-react';
import type { AdminUser, AdminCatch, AdminReview } from '../lib/supabase';
interface BehaviorInsightsProps {
  users: AdminUser[];
  catches: AdminCatch[];
  reviews: AdminReview[];
  /** Exports the joined review × profile rows as CSV. */
  onExport: (rows: JoinedReviewRow[]) => void;
}
/** A single review enriched with the reviewer's profile — the join backbone. */
export interface JoinedReviewRow {
  reviewId: string;
  phone: string;
  name: string;
  occupation: string;
  ageRange: string;
  city: string;
  interests: string[];
  productBrand: string;
  productName: string;
  taste: boolean | null;
  packaging: boolean | null;
  buyAgain: boolean | null;
}
const pct = (part: number, whole: number) => whole > 0 ? Math.round(part / whole * 100) : 0;
const UNKNOWN = 'Unknown';
/**
 * BEHAVIOR INSIGHTS — connects the three data sets (users ⇄ catches ⇄ reviews)
 * so patterns between WHO people are (occupation, age, interests) and HOW they
 * react (taste, packaging, buy-again) become readable. Every review is joined
 * to its reviewer's profile via the shared phone number; no sheet changes are
 * required for any of this.
 */
export function BehaviorInsights({
  users,
  catches,
  reviews,
  onExport
}: BehaviorInsightsProps) {
  const [dimension, setDimension] = useState<'occupation' | 'ageRange' | 'interests'>('occupation');
  const [metric, setMetric] = useState<'taste' | 'packaging' | 'buyAgain'>('taste');
  // Index users by phone for O(1) joins.
  const userByPhone = useMemo(() => {
    const m = new Map<string, AdminUser>();
    users.forEach(u => m.set(u.phone, u));
    return m;
  }, [users]);
  // THE JOIN: every review enriched with its reviewer's profile.
  const joined = useMemo<JoinedReviewRow[]>(() => {
    return reviews.map(r => {
      const u = userByPhone.get(r.userId);
      return {
        reviewId: r.id,
        phone: r.userId,
        name: u?.name || UNKNOWN,
        occupation: u?.occupation || UNKNOWN,
        ageRange: u?.ageRange || UNKNOWN,
        city: u?.city || UNKNOWN,
        interests: u?.interests?.length ? u.interests : [UNKNOWN],
        productBrand: r.productBrand || UNKNOWN,
        productName: r.productName || UNKNOWN,
        taste: r.taste,
        packaging: r.packaging,
        buyAgain: r.buyAgain
      };
    });
  }, [reviews, userByPhone]);
  const metricLabel = {
    taste: 'Liked taste',
    packaging: 'Liked packaging',
    buyAgain: 'Would buy again'
  }[metric];
  const dimensionLabel = {
    occupation: 'Occupation',
    ageRange: 'Age range',
    interests: 'Interest'
  }[dimension];
  // Cross-tab: chosen profile dimension × chosen sentiment metric.
  const crossTab = useMemo(() => {
    const buckets: Record<string, {
      likes: number;
      rated: number;
    }> = {};
    joined.forEach(row => {
      const keys = dimension === 'interests' ? row.interests : [row[dimension] as string];
      const val = row[metric];
      keys.forEach(k => {
        if (!buckets[k]) buckets[k] = {
          likes: 0,
          rated: 0
        };
        if (val !== null) {
          buckets[k].rated += 1;
          if (val === true) buckets[k].likes += 1;
        }
      });
    });
    return Object.entries(buckets).map(([key, v]) => ({
      key,
      likes: v.likes,
      rated: v.rated,
      rate: pct(v.likes, v.rated)
    })).filter(b => b.rated > 0).sort((a, b) => b.rate - a.rate || b.rated - a.rated);
  }, [joined, dimension, metric]);
  // Top product per profile segment (which item resonates with whom).
  const productBySegment = useMemo(() => {
    const map: Record<string, Record<string, {
      likes: number;
      rated: number;
    }>> = {};
    joined.forEach(row => {
      const segs = dimension === 'interests' ? row.interests : [row[dimension] as string];
      const val = row[metric];
      if (val === null) return;
      segs.forEach(seg => {
        if (!map[seg]) map[seg] = {};
        const prod = `${row.productBrand} · ${row.productName}`;
        if (!map[seg][prod]) map[seg][prod] = {
          likes: 0,
          rated: 0
        };
        map[seg][prod].rated += 1;
        if (val === true) map[seg][prod].likes += 1;
      });
    });
    return Object.entries(map).map(([seg, prods]) => {
      const top = Object.entries(prods).map(([prod, v]) => ({
        prod,
        ...v,
        rate: pct(v.likes, v.rated)
      })).filter(p => p.rated >= 1).sort((a, b) => b.rate - a.rate || b.rated - a.rated)[0];
      return top ? {
        seg,
        ...top
      } : null;
    }).filter(Boolean).sort((a: any, b: any) => b.rate - a.rate) as {
      seg: string;
      prod: string;
      rate: number;
      rated: number;
      likes: number;
    }[];
  }, [joined, dimension, metric]);
  // Headline numbers.
  const totalReviews = reviews.length;
  const reviewers = useMemo(() => new Set(reviews.map(r => r.userId)).size, [reviews]);
  // Review completion: of caught (redeemed/reviewed) boxes, how many reviewed.
  const caughtCount = useMemo(() => catches.filter(c => c.status === 'redeemed' || c.status === 'reviewed').length, [catches]);
  const reviewedCount = useMemo(() => catches.filter(c => c.status === 'reviewed').length, [catches]);
  if (totalReviews === 0) {
    return <div className="bg-gray-900 rounded-2xl p-10 border border-gray-800 text-center" data-id="element-119" data-mp="1p9xv268w7gjq">
        <Brain className="w-10 h-10 text-gray-600 mx-auto mb-3" data-id="element-120" data-mp="237w7v77kf4x4" />
        <h3 className="text-white font-bold mb-1" data-id="element-121" data-mp="236bazaxs39w3">No behavior data yet</h3>
        <p className="text-gray-500 text-sm" data-id="element-122" data-mp="1ybe70ifcvqq2">
          Once people review their catches, this view connects their reviews to
          their occupation, age and interests.
        </p>
      </div>;
  }
  return <div className="space-y-6" data-id="element-123" data-mp="10t5agde4m4ts">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-124" data-mp="14wbi9pgw042d">
        <div data-id="element-125" data-mp="14v0gfv7056dw">
          <h2 className="text-lg font-bold flex items-center" data-id="element-126" data-mp="19nbf6uq0am4v">
            <Brain className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-127" data-mp="19po4xu20a0sh" />
            Behavior Insights
          </h2>
          <p className="text-sm text-gray-400" data-id="element-128" data-mp="1eor2ktfnp4qf">
            How taste, packaging &amp; repurchase intent vary across who people
            are.
          </p>
        </div>
        <button onClick={() => onExport(joined)} className="flex items-center text-sm text-blue-400 hover:text-blue-300" data-id="element-129" data-mp="1olc5n98485rm">
          <Download className="w-4 h-4 mr-1" data-id="element-130" data-mp="3qcgvq0uegy9u" /> Export joined data
        </button>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-131" data-mp="3ud0uf8umg9y3">
        <Stat icon={Star} label="Reviews" value={String(totalReviews)} data-id="element-132" data-mp="3mu9l6nbwf9m8" />
        <Stat icon={Users} label="Reviewers" value={String(reviewers)} data-id="element-133" data-mp="3mvkqwsowmrfz" />
        <Stat icon={TrendingUp} label="Review completion" value={`${pct(reviewedCount, caughtCount)}%`} sub={`${reviewedCount}/${caughtCount} caught`} data-id="element-134" data-mp="3i2rkm0dsfb1s" />
        <Stat icon={Package} label="Would buy again" value={`${pct(joined.filter(j => j.buyAgain === true).length, joined.filter(j => j.buyAgain !== null).length)}%`} data-id="element-135" data-mp="394656e3m4c58" />
      </div>

      {/* Controls */}
      <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800 space-y-4" data-id="element-136" data-mp="n1m22rknyyn">
        <div data-id="element-137" data-mp="p5yxa7g88b2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-138" data-mp="3rtcu4iv01zzh">
            Group people by
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-139" data-mp="2ldk8tk2z0gb2">
            {([['occupation', 'Occupation', Briefcase], ['ageRange', 'Age range', Users], ['interests', 'Interests', Heart]] as const).map(([id, label, Icon]) => <button key={id} onClick={() => setDimension(id)} className={`flex items-center text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors ${dimension === id ? 'bg-brand-blue text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`} data-id="element-140" data-mp="odpgx52w5lo9">
                <Icon className="w-3.5 h-3.5 mr-1.5" data-id="element-141" data-mp="133n8zw3xyviq" />
                {label}
              </button>)}
          </div>
        </div>
        <div data-id="element-142" data-mp="tciyb8latwtp">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-143" data-mp="tapltco08ids">
            Measure
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-144" data-mp="y8zl9zgri557">
            {([['taste', 'Liked taste'], ['packaging', 'Liked packaging'], ['buyAgain', 'Would buy again']] as const).map(([id, label]) => <button key={id} onClick={() => setMetric(id)} className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors ${metric === id ? 'bg-brand-yellow text-brand-navy' : 'bg-gray-800 text-gray-400 hover:text-white'}`} data-id="element-145" data-mp="3bg8wbgw6mbpe">
                {label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Cross-tab: dimension × metric */}
      <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-146" data-mp="2nk4pekhy2lhp">
        <h3 className="font-bold text-white mb-1" data-id="element-147" data-mp="31jcozei0rvmf">
          {metricLabel} by {dimensionLabel}
        </h3>
        <p className="text-xs text-gray-500 mb-4" data-id="element-148" data-mp="2wkj779vp9igg">
          Share who answered “yes”, within each group that left a rating.
        </p>
        <div className="space-y-3" data-id="element-149" data-mp="gru1jgn57fj3">
          {crossTab.map(b => <div key={b.key} data-id="element-150" data-mp="buk4pfxfc1l8">
              <div className="flex justify-between text-xs mb-1" data-id="element-151" data-mp="bt93nnvd57xt">
                <span className="text-gray-300" data-id="element-152" data-mp="261wh1r4ecnax">{b.key}</span>
                <span className="text-white font-bold" data-id="element-153" data-mp="263hfhrtu0qe2">
                  {b.rate}%{' '}
                  <span className="text-gray-500 font-normal" data-id="element-154" data-mp="214d4qwr9eklm">
                    ({b.likes}/{b.rated})
                  </span>
                </span>
              </div>
              <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden" data-id="element-155" data-mp="1s96u9kh1necr">
                <div className="bg-brand-yellow h-full rounded-full transition-all" style={{
              width: `${b.rate}%`
            }} data-id="element-156" data-mp="1nb59mv3yz6z6" />
              </div>
            </div>)}
        </div>
      </div>

      {/* Top product per segment */}
      <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-157" data-mp="19cfc5ed8n5aa">
        <h3 className="font-bold text-white mb-1" data-id="element-158" data-mp="37r0ah9gu37zo">
          Top product per {dimensionLabel.toLowerCase()}
        </h3>
        <p className="text-xs text-gray-500 mb-4" data-id="element-159" data-mp="33qga7x0yro9b">
          The item each group rated highest on “{metricLabel.toLowerCase()}”.
        </p>
        <div className="overflow-x-auto" data-id="element-160" data-mp="uggzm94rq9rb">
          <table className="w-full text-sm" data-id="element-161" data-mp="uffcv7ee3ytr">
            <thead data-id="element-162" data-mp="ue48b5d4en1l">
              <tr className="text-left text-gray-500 text-xs uppercase tracking-wider" data-id="element-163" data-mp="z7fi9fd8bjxq">
                <th className="py-2 pr-4 font-bold" data-id="element-164" data-mp="138r9tqfnyf8k">{dimensionLabel}</th>
                <th className="py-2 pr-4 font-bold" data-id="element-165" data-mp="137zcaa2ia7fn">Top product</th>
                <th className="py-2 font-bold text-right" data-id="element-166" data-mp="6jjn4f3b6032">{metricLabel}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800" data-id="element-167" data-mp="bi4pak24etu5">
              {productBySegment.map(row => <tr key={row.seg} data-id="element-168" data-mp="fhn0go453qjs">
                  <td className="py-2.5 pr-4 text-gray-300" data-id="element-169" data-mp="ki9m609ou9u2">{row.seg}</td>
                  <td className="py-2.5 pr-4 text-white font-medium" data-id="element-170" data-mp="kg6kuk334ua1">
                    {row.prod}
                  </td>
                  <td className="py-2.5 text-right text-brand-yellow font-bold" data-id="element-171" data-mp="1vxg16xqx3674">
                    {row.rate}%{' '}
                    <span className="text-gray-500 font-normal text-xs" data-id="element-172" data-mp="3s01ktpi3cegu">
                      ({row.likes}/{row.rated})
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function Stat({
  icon: Icon,
  label,
  value,
  sub
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
  sub?: string;
}) {
  return <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800" data-id="element-173" data-mp="3980gi7mjrw01">
      <div className="flex items-center text-gray-400 text-[11px] font-bold uppercase tracking-wider mb-2" data-id="element-174" data-mp="34cb9xkc41eu8">
        <Icon className="w-4 h-4 mr-2 text-brand-yellow" data-id="element-175" data-mp="3j1h8d634j112" />
        {label}
      </div>
      <div className="text-2xl font-bold text-white" data-id="element-176" data-mp="3e8eesxycjrkv">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1" data-id="element-177" data-mp="2qj29i96543nu">{sub}</div>}
    </div>;
}